import { useCallback, useEffect, useState } from 'react';
import type { OrderSummaryDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';
import { apiFetch } from '../api/client.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { OrderDetailPanel } from '../components/OrderDetailPanel.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { labels } from '../labels.js';

const PAGE_SIZE = 20;

export function HistoriquePage() {
  const [orders, setOrders] = useState<OrderSummaryDTO[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const statuses = ['SERVED', 'COMPLETED', 'CANCELLED'].join(',');
    return apiFetch<{ items: OrderSummaryDTO[]; totalPages: number }>(
      `/admin/orders?status=${statuses}&page=${page}&pageSize=${PAGE_SIZE}&sort=placedAt&order=desc`,
    )
      .then((d) => { setOrders(d.items); setTotalPages(d.totalPages); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.history.title}</h1>
          <p className="page-subtitle">{labels.history.subtitle}</p>
        </div>
      </header>

      {loadError ? (
        <ErrorState onRetry={load} />
      ) : !loading && orders.length === 0 ? (
        <div className="card">
          <EmptyState icon="history" text={labels.history.empty} />
        </div>
      ) : (
        <div className="card card--flush">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{labels.history.orderColumn}</th>
                  <th>{labels.history.tableColumn}</th>
                  <th>{labels.history.timeColumn}</th>
                  <th className="num">{labels.history.itemsColumn}</th>
                  <th className="num">{labels.history.totalColumn}</th>
                  <th>{labels.history.paymentColumn}</th>
                  <th>{labels.history.statusColumn}</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        <td colSpan={7}><div className="skeleton" style={{ height: '1.25rem' }} /></td>
                      </tr>
                    ))
                  : orders.map((o) => (
                      <tr key={o.id} className="is-clickable" onClick={() => setSelectedOrderId(o.id)}>
                        <td>
                          <button
                            type="button"
                            className="row-link"
                            onClick={(e) => { e.stopPropagation(); setSelectedOrderId(o.id); }}
                          >
                            #{o.number}
                          </button>
                        </td>
                        <td>{labels.app.tableLabel(o.table.label)}</td>
                        <td className="tabular text-muted">
                          {new Date(o.placedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="num">{o.itemCount}</td>
                        <td className="num" style={{ fontWeight: 'var(--weight-semibold)' }}>{formatMoney(o.totalMinor, o.currency)}</td>
                        <td className="text-muted">{o.payment ? labels.payment.methodLabels[o.payment.method] : '-'}</td>
                        <td><StatusBadge status={o.status} /></td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <footer className="card-footer" style={{ justifyContent: 'space-between' }}>
              <span className="text-sm text-muted tabular">{labels.history.pageOf(page, totalPages)}</span>
              <div className="row">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost btn-sm">
                  <Icon name="chevron-left" size={16} />
                  {labels.history.previousPage}
                </button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost btn-sm">
                  {labels.history.nextPage}
                  <Icon name="chevron-right" size={16} />
                </button>
              </div>
            </footer>
          )}
        </div>
      )}

      {selectedOrderId && (
        <OrderDetailPanel orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />
      )}
    </div>
  );
}
